import { useEffect, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useToast } from "@/components/ui/use-toast";
import { authService } from "@/services/auth";
import useSendPhone2FA from "@/features/phone-verification/hooks/useSendPhone2FA.tsx";
import useVerifyPhone2FA from "@/features/phone-verification/hooks/useVerifyPhone2FA.tsx";
import { routes } from "@/router";
import EnterPhoneCode from "../Home/card/view/_components/EnterPhoneCode";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button/button";
import { RequestError } from "@/axios/schema";
import { VerifyPhone2FAPayload } from "@/api/phone-auth.ts";

const VerifyPhoneCodePage = () => {
	const { phone, country, log_id, candidate_ids } =
		routes.public.verifyPhoneCode.useSearch();
	const navigate = useNavigate();
	const { toast } = useToast();

	const [code, setCode] = useState("");
	const inputRef = useRef<HTMLInputElement>(null);

	const verifyCode = useVerifyPhone2FA();
	const resendCode = useSendPhone2FA();

	const [showIncorrectCodeDialog, setShowIncorrectCodeDialog] = useState(false);

	useEffect(() => {
		inputRef.current?.focus();
	}, []);

	const onSubmit = (digits: number) => {
		let phone_fallback: VerifyPhone2FAPayload["phone_fallback"] = undefined;
		if (log_id && candidate_ids) {
			phone_fallback = {
				biometric_sync_id: log_id,
				candidate_ids: candidate_ids,
			};
		}
		verifyCode.mutate(
			{
				payload: {
					phone_number: phone,
					global_country_id: Number(country),
					verification_code: digits.toString(),
					phone_fallback,
				},
			},
			{
				onSuccess: (token) => {
					authService.setToken(token);
					navigate({ to: "/main/home" });
				},
				onError: () => {
					setShowIncorrectCodeDialog(true);
					setCode("");
				},
			},
		);
	};

	const onResendCode = () => {
		resendCode.mutate(
			{
				payload: {
					phone_number: phone,
					global_country_id: Number(country),
				},
			},
			{
				onSuccess: () => {
					toast({
						title: "Code Resent",
						description: "A new verification code has been sent to your phone.",
					});
				},
				onError: (error: RequestError) => {
					toast({
						title: "Failed to resend code",
						description:
							error?.message ??
							"We couldn't resend the code. Please try again.",
						variant: "destructive",
					});
				},
			},
		);
	};

	return (
		<>
			<EnterPhoneCode
				phone={phone}
				code={code}
				setCode={setCode}
				onSubmit={onSubmit}
				onResendCode={onResendCode}
				submitMutationStatus={verifyCode.status}
				resendMutationStatus={resendCode.status}
			/>

			{/* Incorrect code dialog */}
			<Dialog
				open={showIncorrectCodeDialog}
				onOpenChange={setShowIncorrectCodeDialog}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Incorrect code</DialogTitle>
						<DialogDescription>
							The verification code you entered is incorrect. Please try again
							or continue with email login.
						</DialogDescription>
					</DialogHeader>
					<DialogFooter className="flex gap-2 sm:justify-end">
						<Button
							size="md"
							variant="outline"
							onClick={() => setShowIncorrectCodeDialog(false)}
						>
							OK
						</Button>
						<Button
							size="md"
							variant="default"
							onClick={() => {
								setShowIncorrectCodeDialog(false);
								navigate({ to: "/sign-mail" });
							}}
						>
							Login with email
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
};

export default VerifyPhoneCodePage;
